/**
 * New-plan wizard (W0) — a 3-step modal: pick a well, pick the event type
 * (letter + conventional), confirm and create a Draft v1. On create it calls
 * pApi.createPlan against the well's matching event (creating the event's first
 * plan) and routes into the Plan Workspace (W1). Ports the wireframe wizard.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@app/hooks";
import { setActiveEvent, setActivePlan, selectWell } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { useGetWellsQuery } from "@api/mApi";
import { pApi, useCreatePlanMutation } from "@api/pApi";
import { store } from "@app/store";
import type { WellView } from "@api/mappers";
import type { EventOut } from "@api/types";
import { EVENT_LETTER_OP_VAR, type EventLetter } from "@lib/eventEnrichment";
import { NEWPLAN_TYPES, NEWPLAN_SUB } from "./planCatalog";

interface WizardType {
  letter: EventLetter;
  conv: boolean;
  code: string;
}

export interface NewPlanWizardProps {
  open: boolean;
  onClose: () => void;
  /** Optionally pre-seed the well (and letter) — e.g. from a row's "New plan". */
  seedWell?: WellView | null;
  seedLetter?: EventLetter | null;
  seedConv?: boolean | null;
}

export function NewPlanWizard({ open, onClose, seedWell, seedLetter, seedConv }: NewPlanWizardProps) {
  const { t } = useLocale();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: wells = [] } = useGetWellsQuery();
  const [createPlan, { isLoading: creating }] = useCreatePlanMutation();

  const [step, setStep] = useState(1);
  const [well, setWell] = useState<WellView | null>(null);
  const [type, setType] = useState<WizardType | null>(null);
  const [search, setSearch] = useState("");

  // (Re)initialize whenever opened, honoring any seed.
  useEffect(() => {
    if (!open) return;
    setWell(seedWell ?? null);
    setSearch("");
    if (seedLetter) {
      const nt = NEWPLAN_TYPES.find((x) => x.letter === seedLetter);
      const conv = seedConv !== false;
      setType(nt ? { letter: seedLetter, conv, code: conv ? nt.codeConv : nt.codeUnconv } : null);
      setStep(seedWell ? 2 : 1);
    } else {
      setType(null);
      setStep(1);
    }
  }, [open, seedWell, seedLetter, seedConv]);

  const filteredWells = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return wells;
    return wells.filter(
      (w) =>
        w.commonName.toLowerCase().includes(q) ||
        w.uwi.toLowerCase().includes(q) ||
        w.field.toLowerCase().includes(q) ||
        w.pad.toLowerCase().includes(q),
    );
  }, [wells, search]);

  if (!open) return null;

  const canNext = step === 1 ? !!well : step === 2 ? !!type : true;

  async function handleCreate() {
    if (!well || !type) return;
    // Find (or accept) the event on this well matching the chosen code. In the
    // mock, events are pre-seeded; we create the plan against the first event
    // whose code matches, else the first event of that letter.
    const events = readEvents(well.uwi);
    const target =
      events.find((e) => e.event_code === type.code) ??
      events.find((e) => e.event_code.startsWith(type.letter)) ??
      events[0];
    if (!target) {
      onClose();
      return;
    }
    try {
      const plan = await createPlan({
        eventId: target.id,
        body: { event_code: type.code, is_conventional: type.conv },
      }).unwrap();
      dispatch(selectWell(well.id));
      dispatch(setActiveEvent(target.id));
      dispatch(setActivePlan(plan.id));
      onClose();
      navigate("/workspace");
    } catch {
      onClose();
    }
  }

  return (
    <div
      className="modal-scrim open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="newPlanTitle"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal">
        <div className="modal-hd">
          <h2 id="newPlanTitle">{t("wizard.title")}</h2>
          <button type="button" className="modal-close" aria-label={t("wizard.cancel")} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-bd">
          <div className="wiz-steps" aria-hidden="true">
            <div className={`ws${step === 1 ? " active" : step > 1 ? " done" : ""}`}>
              <span className="n">1</span>
              <span className="lbl">{t("wizard.step.well")}</span>
            </div>
            <div className="bar" />
            <div className={`ws${step === 2 ? " active" : step > 2 ? " done" : ""}`}>
              <span className="n">2</span>
              <span className="lbl">{t("wizard.step.eventType")}</span>
            </div>
            <div className="bar" />
            <div className={`ws${step === 3 ? " active" : ""}`}>
              <span className="n">3</span>
              <span className="lbl">{t("wizard.step.confirm")}</span>
            </div>
          </div>

          {/* STEP 1 — pick well */}
          {step === 1 && (
            <div>
              <div className="typeahead" style={{ marginBottom: 12 }}>
                <span className="ic ic-search" aria-hidden="true" style={{ top: "50%" }} />
                <input
                  type="search"
                  placeholder={t("wizard.searchWellPlaceholder")}
                  aria-label={t("wizard.searchWell")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="well-pick-list" role="listbox" aria-label={t("wizard.pickWell")}>
                {filteredWells.length === 0 && (
                  <div className="dim" style={{ padding: 14, textAlign: "center", fontSize: 13 }}>
                    {t("wizard.noMatch")}
                  </div>
                )}
                {filteredWells.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    className={`well-pick${well?.id === w.id ? " selected" : ""}`}
                    role="option"
                    aria-selected={well?.id === w.id}
                    onClick={() => setWell(w)}
                  >
                    <span className="av">{w.commonName.slice(0, 2).toUpperCase()}</span>
                    <span className="nm">
                      <b>{w.commonName}</b>
                      <small>
                        {w.uwi} · {w.field} · {w.pad}
                      </small>
                    </span>
                    <span className="check" aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 — pick event type */}
          {step === 2 && (
            <div>
              <div className="chip-ctx" style={{ marginBottom: 14 }}>
                <span className="k">{t("wizard.chosenWell")}</span>
                <span className="mono">{well ? `${well.commonName} · ${well.uwi}` : "—"}</span>
              </div>
              <label
                style={{
                  display: "block",
                  color: "var(--dim)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  marginBottom: 8,
                }}
              >
                {t("wizard.eventTypePrompt")}
              </label>
              <div className="evt-type-grid">
                {NEWPLAN_TYPES.map((nt) => {
                  const selected = type?.letter === nt.letter;
                  return (
                    <button
                      key={nt.letter}
                      type="button"
                      className={`evt-type-card${selected ? " selected" : ""}`}
                      onClick={() =>
                        setType({
                          letter: nt.letter,
                          conv: selected ? type!.conv : true,
                          code: selected && !type!.conv ? nt.codeUnconv : nt.codeConv,
                        })
                      }
                    >
                      <span className="hd">
                        <span
                          className="letter"
                          style={{ background: `var(${EVENT_LETTER_OP_VAR[nt.letter]})` }}
                        >
                          {nt.letter}
                        </span>
                        <span className="ttl">{t(nt.titleKey)}</span>
                      </span>
                      <span className="sub">{NEWPLAN_SUB[nt.subKey]}</span>
                      <span className="conv-toggle">
                        <span
                          className={`conv-opt${selected && type!.conv ? " active" : ""}`}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setType({ letter: nt.letter, conv: true, code: nt.codeConv });
                          }}
                        >
                          {t("conv.convention")}
                        </span>
                        <span
                          className={`conv-opt${selected && !type!.conv ? " active" : ""}`}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setType({ letter: nt.letter, conv: false, code: nt.codeUnconv });
                          }}
                        >
                          {t("conv.unconvention")}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 3 — confirm */}
          {step === 3 && well && type && (
            <div>
              <div className="section-note" style={{ marginBottom: 14 }}>
                {t("wizard.note")}
              </div>
              <div className="card" style={{ padding: "14px 16px" }}>
                <div className="summary-row">
                  <span className="k">{t("wizard.sum.well")}</span>
                  <span className="v">{well.commonName}</span>
                </div>
                <div className="summary-row">
                  <span className="k">{t("wizard.sum.uwi")}</span>
                  <span className="v mono">{well.uwi}</span>
                </div>
                <div className="summary-row">
                  <span className="k">{t("wizard.sum.eventType")}</span>
                  <span className="v">
                    {type.code} — {t(NEWPLAN_TYPES.find((x) => x.letter === type.letter)!.titleKey)}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="k">{t("wizard.sum.conv")}</span>
                  <span className="v">{type.conv ? t("conv.convention") : t("conv.unconvention")}</span>
                </div>
                <div className="summary-row">
                  <span className="k">{t("wizard.sum.afe")}</span>
                  <span className="v mono">{t("wizard.sum.afeAuto")}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-ft">
          <button
            type="button"
            className="btn ghost"
            style={{ visibility: step === 1 ? "hidden" : "visible" }}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
          >
            {t("wizard.back")}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>
              {t("wizard.cancel")}
            </button>
            {step < 3 ? (
              <button
                type="button"
                className="btn primary"
                disabled={!canNext}
                onClick={() => setStep((s) => s + 1)}
              >
                {t("wizard.next")}
              </button>
            ) : (
              <button type="button" className="btn primary" disabled={creating} onClick={handleCreate}>
                {creating ? t("wizard.creating") : t("wizard.create")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Read events for a well from the pApi cache (they were prefetched by the finder).
  function readEvents(uwi: string): EventOut[] {
    const sel = pApi.endpoints.getWellEvents.select(uwi);
    return sel(store.getState()).data ?? [];
  }
}
